/**
 * Datum och tid, alltid svenskt.
 *
 * Webbläsarens språkinställning avgör annars formatet, och en dator satt till
 * en-US visar 7/31/2026 3:00 PM mitt i ett svenskt gränssnitt. Locale låses
 * därför explicit i stället för att ärvas.
 *
 * Tidszonen låses INTE. starts_at och lock_time är absoluta tidpunkter, och
 * att visa dem i användarens egen zon är rätt — men fälten märks med zonen så
 * det aldrig råder tvekan om vilken klocka som gäller.
 */

const LOCALE = 'sv-SE'

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '–'
  return new Date(iso).toLocaleString(LOCALE, {
    dateStyle: 'medium',
    timeStyle: 'short',
    hourCycle: 'h23',
  })
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '–'
  return new Date(iso).toLocaleDateString(LOCALE, { dateStyle: 'full' })
}

export function formatDateShort(iso: string | null | undefined): string {
  if (!iso) return '–'
  return new Date(iso).toLocaleDateString(LOCALE, { dateStyle: 'medium' })
}

export function formatTime(iso: string | null | undefined): string {
  if (!iso) return '–'
  return new Date(iso).toLocaleTimeString(LOCALE, {
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  })
}

/** "CEST" eller "GMT+2" — vilken klocka fälten faktiskt visar. */
export function localZoneLabel(): string {
  const parts = new Intl.DateTimeFormat(LOCALE, { timeZoneName: 'short' }).formatToParts(new Date())
  return parts.find((p) => p.type === 'timeZoneName')?.value ?? ''
}

/** timestamptz → värdet ett datetime-local-fält vill ha, i användarens zon. */
export function toLocalInput(iso: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

export function fromLocalInput(local: string): string | null {
  return local ? new Date(local).toISOString() : null
}

/**
 * Läser tillbaka ett `datetime-local`-värde på svenska.
 *
 * Fältets egen rendering — am/pm, vilken dag veckan börjar på — styrs av
 * webbläsarens språkinställning, inte av sidans `lang` och inte av `lang` på
 * elementet. Det går alltså inte att rätta från appen.
 *
 * Det som går att göra är att visa vad som faktiskt valdes, otvetydigt. Ett
 * fel `lock_time` har redan kostat en felsökning, och skillnaden mellan 01:00
 * och 13:00 är exakt den sorts miss en am/pm-väljare inbjuder till.
 */
export function describeLocal(localInput: string): string {
  const d = new Date(localInput)
  if (Number.isNaN(d.getTime())) return ''
  return new Intl.DateTimeFormat('sv-SE', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(d)
}
